import {
    AxiosError,
    AxiosInstance,
    InternalAxiosRequestConfig,
} from "axios";

import { refreshManager } from "./refresh.manager";

type RetryRequest = InternalAxiosRequestConfig & {
    _retry?: boolean;
    /**
     * Permet à certaines requêtes de bypass le refresh automatique
     * (ex: /auth/login, /auth/register, /auth/refresh).
     */
    _skipAuthRefresh?: boolean;
};

/**
 * Routes pour lesquelles on ne tente JAMAIS de refresh.
 */
const SKIP_REFRESH_PATTERNS = [
    "/auth/refresh",
    "/auth/login",
    "/auth/register",
    "/auth/forgot-password",
    "/auth/verify-reset-code",
    "/auth/reset-password",
];

const shouldSkipRefresh = (url: string): boolean => {
    return SKIP_REFRESH_PATTERNS.some((pattern) => url.includes(pattern));
};

export const setupInterceptors = (client: AxiosInstance): void => {
    client.interceptors.response.use(
        (response) => response,

        async (error: AxiosError) => {
            const status = error.response?.status;
            const originalRequest = error.config as RetryRequest;

            // Pas un 401 → on rejette tel quel
            if (status !== 401) {
                return Promise.reject(error);
            }

            if (!originalRequest) {
                return Promise.reject(error);
            }

            const requestUrl = originalRequest.url ?? "";

            // Skip si route sensible
            if (
                originalRequest._skipAuthRefresh ||
                shouldSkipRefresh(requestUrl)
            ) {
                return Promise.reject(error);
            }

            // Anti-boucle
            if (originalRequest._retry) {
                return Promise.reject(error);
            }

            originalRequest._retry = true;

            try {
                await refreshManager.refresh(client);

                // Rejoue la requête originale (le nouveau cookie est en place)
                return client(originalRequest);
            } catch (refreshError) {
                // Le RefreshManager a déjà émis l'event si trop d'échecs.
                // On rejette simplement l'erreur pour laisser le contexte
                // React gérer le logout.
                return Promise.reject(refreshError);
            }
        },
    );
};